import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar as CalendarIcon, ArrowRight, Clock, MapPin } from "lucide-react";
import { STUDIO_VISIT } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compute the next 8 Wednesdays from today
function nextWednesdays(count = 8) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = today.getDay(); // 0=Sun, 3=Wed
  const offset = (3 - day + 7) % 7 || 7; // always next Wed (not today)
  const first = new Date(today);
  first.setDate(today.getDate() + offset);
  return Array.from({ length: count }).map((_, i) => {
    const d = new Date(first);
    d.setDate(first.getDate() + i * 7);
    return d;
  });
}

function fmtDate(d) {
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export default function StudioVisit() {
  const wednesdays = useMemo(() => nextWednesdays(8), []);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_date: wednesdays[0].toISOString().slice(0, 10),
    party_size: 1,
    role: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: k === "party_size" ? parseInt(e.target.value || "1", 10) : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/studio-visit`, form);
      toast.success("Visit request received. The studio will confirm within 48 hours.");
      setDone(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't send. Please try again.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="studio-visit-page">
      <header className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
        <p className="label mb-4">By Appointment</p>
        <h1 className="serif text-5xl md:text-7xl tracking-tight font-light leading-[1.02]">
          Visit the studio.
        </h1>
        <p className="mt-6 max-w-2xl text-[hsl(var(--ink-soft))] leading-relaxed text-lg">
          A small number of studio visits are scheduled each month — reserved
          for collectors, curators, and press who want to spend time with the
          work in person.
        </p>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-32 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        <aside className="md:col-span-5 space-y-8">
          <InfoRow icon={<Clock className="h-4 w-4" />} label="Hours" value={STUDIO_VISIT.hours} />
          <InfoRow icon={<MapPin className="h-4 w-4" />} label="Location" value={STUDIO_VISIT.location} />
          <InfoRow icon={<CalendarIcon className="h-4 w-4" />} label="Booking" value="Wednesday evenings only" />

          <div className="border-t border-border pt-6 text-sm text-[hsl(var(--ink-soft))] leading-relaxed">
            <p>{STUDIO_VISIT.notes}</p>
          </div>

          <div className="border-t border-border pt-6">
            <p className="label mb-2">Not a collector? Still welcome.</p>
            <p className="text-sm text-[hsl(var(--ink-soft))]">
              If you're a student, educator, or community organizer, mention it in
              your message — Joseph keeps a few slots each month for public visits.
            </p>
          </div>
        </aside>

        <div className="md:col-span-7">
          {done ? (
            <div className="border border-border p-10" data-testid="studio-visit-thanks">
              <p className="label mb-4">Requested</p>
              <h2 className="serif text-4xl md:text-5xl tracking-tight">
                Your visit request is in.
              </h2>
              <p className="mt-4 text-[hsl(var(--ink-soft))] leading-relaxed">
                Joseph will confirm or propose an alternate Wednesday within 48
                hours, along with the studio address.
              </p>
              <p className="mt-6 label">
                Requested for{" "}
                {fmtDate(new Date(done.preferred_date + "T17:00:00"))}
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="studio-visit-form" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Name">
                  <input
                    required
                    value={form.name}
                    onChange={set("name")}
                    className="sv-input"
                    data-testid="sv-name"
                  />
                </Field>
                <Field label="Email">
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    className="sv-input"
                    data-testid="sv-email"
                  />
                </Field>
                <Field label="Phone (optional)">
                  <input
                    value={form.phone}
                    onChange={set("phone")}
                    className="sv-input"
                    data-testid="sv-phone"
                  />
                </Field>
                <Field label="You are">
                  <select
                    value={form.role}
                    onChange={set("role")}
                    className="sv-input"
                    data-testid="sv-role"
                  >
                    <option value="">Select…</option>
                    <option>Collector</option>
                    <option>Curator</option>
                    <option>Press / Media</option>
                    <option>Educator / Student</option>
                    <option>Other</option>
                  </select>
                </Field>
                <Field label="Preferred Wednesday">
                  <select
                    required
                    value={form.preferred_date}
                    onChange={set("preferred_date")}
                    className="sv-input"
                    data-testid="sv-date"
                  >
                    {wednesdays.map((d) => {
                      const iso = d.toISOString().slice(0, 10);
                      return (
                        <option key={iso} value={iso}>
                          {fmtDate(d)} · 5–8 PM
                        </option>
                      );
                    })}
                  </select>
                </Field>
                <Field label="Party size">
                  <input
                    type="number"
                    min={1}
                    max={6}
                    value={form.party_size}
                    onChange={set("party_size")}
                    className="sv-input"
                    data-testid="sv-party"
                  />
                </Field>
              </div>
              <Field label="What would you like to see, or ask about?">
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  className="sv-input resize-none"
                  data-testid="sv-message"
                  placeholder="Pieces you'd like to view, the series you're following, what brought you to the work…"
                />
              </Field>
              <button
                disabled={busy}
                className="inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 text-xs uppercase tracking-[0.18em] hover:opacity-90 disabled:opacity-50"
                data-testid="sv-submit"
              >
                {busy ? "Requesting…" : "Request a visit"}
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
              <p className="text-xs text-[hsl(var(--ink-soft))] mt-4 max-w-md">
                Your request is reviewed personally. Contact details stay
                private — used only to confirm the visit.
              </p>
            </form>
          )}
        </div>
      </div>

      <style>{`
        .sv-input { width: 100%; background: transparent; border: none; border-bottom: 1px solid hsl(var(--border)); padding: 0.6rem 0; font-size: 1rem; color: hsl(var(--ink)); outline: none; transition: border-color 0.3s ease; }
        .sv-input:focus { border-color: hsl(var(--ink)); }
        textarea.sv-input { border: 1px solid hsl(var(--border)); padding: 1rem; }
        textarea.sv-input:focus { border-color: hsl(var(--ink)); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label mb-2 block">{label}</span>
      {children}
    </label>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-1 text-[hsl(var(--accent))]">{icon}</span>
      <div>
        <p className="label">{label}</p>
        <p className="serif text-xl mt-1">{value}</p>
      </div>
    </div>
  );
}
